'use client'

import Image from 'next/image'
import { useState, useRef, ReactNode, useMemo } from 'react'
import { User, ImageIcon, Briefcase } from 'lucide-react'

/**
 * ResponsiveImage - Standardized image component for consistent scaling across the platform
 *
 * Solves the following issues:
 * - Inconsistent image cropping across cards
 * - Circular avatar face cropping
 * - Missing fallback handling
 * - Inconsistent responsive sizing
 *
 * @example
 * // Card image (event/course cards)
 * <ResponsiveImage src={url} alt="Event" variant="card" />
 *
 * // Avatar (circular, with fallback icon)
 * <ResponsiveImage src={url} alt="User" variant="avatar" size="md" />
 *
 * // Thumbnail (square, for file previews)
 * <ResponsiveImage src={url} alt="File" variant="thumbnail" size="lg" />
 *
 * // Banner (wide aspect ratio)
 * <ResponsiveImage src={url} alt="Banner" variant="banner" />
 *
 * // Custom with smart fit (auto-detects landscape vs portrait)
 * <ResponsiveImage src={url} alt="Custom" fit="smart" aspectRatio="4/3" />
 */

// Predefined sizes for avatars and thumbnails
const AVATAR_SIZES = {
  xs: { width: 24, height: 24, className: 'w-6 h-6' },
  sm: { width: 32, height: 32, className: 'w-8 h-8' },
  md: { width: 40, height: 40, className: 'w-10 h-10' },
  lg: { width: 56, height: 56, className: 'w-14 h-14' },
  xl: { width: 80, height: 80, className: 'w-20 h-20' },
  '2xl': { width: 96, height: 96, className: 'w-24 h-24' },
  '3xl': { width: 128, height: 128, className: 'w-32 h-32' },
} as const

type AvatarSize = keyof typeof AVATAR_SIZES

// Predefined aspect ratios
const ASPECT_RATIOS = {
  square: 'aspect-square',      // 1:1
  video: 'aspect-video',        // 16:9
  card: 'aspect-[4/3]',         // 4:3 (good for cards)
  banner: 'aspect-[21/9]',      // 21:9 (cinematic banner)
  portrait: 'aspect-[3/4]',     // 3:4 (portrait photos)
  wide: 'aspect-[2/1]',         // 2:1 (wide banner)
} as const

type AspectRatio = keyof typeof ASPECT_RATIOS

// Variant configurations
type ImageVariant = 'card' | 'avatar' | 'thumbnail' | 'banner' | 'speaker' | 'partner' | 'custom'

// Object fit options
type ObjectFit = 'cover' | 'contain' | 'smart'

interface ResponsiveImageProps {
  /** Image source URL */
  src: string | null | undefined
  /** Alt text for accessibility */
  alt: string
  /** Predefined variant for common use cases */
  variant?: ImageVariant
  /** Size for avatar/thumbnail variants */
  size?: AvatarSize
  /** Aspect ratio for card/banner variants */
  aspectRatio?: AspectRatio | string
  /** Object fit strategy */
  fit?: ObjectFit
  /** Shape of the image container */
  shape?: 'rounded' | 'circle' | 'none'
  /** Custom fallback element when no image */
  fallback?: ReactNode
  /** Additional container className */
  containerClassName?: string
  /** Additional image className */
  imageClassName?: string
  /** Whether to show hover effect */
  hoverScale?: boolean
  /** Background color for letterboxing */
  bgColor?: string
  /** Ring/border style */
  ring?: 'none' | 'default' | 'hover'
  /** Priority loading */
  priority?: boolean
  /** Responsive sizes attribute */
  sizes?: string
  /** onClick handler */
  onClick?: () => void
}

export default function ResponsiveImage({
  src,
  alt,
  variant = 'custom',
  size = 'md',
  aspectRatio,
  fit,  // No default - uses variant's defaultFit
  shape,
  fallback,
  containerClassName = '',
  imageClassName = '',
  hoverScale = false,
  bgColor = 'bg-gray-100',
  ring = 'none',
  priority = false,
  sizes,
  onClick,
}: ResponsiveImageProps) {
  const [hasError, setHasError] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [naturalDimensions, setNaturalDimensions] = useState<{ width: number; height: number } | null>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // Generate a unique key for the image to force remount on src change
  // This is the React-recommended pattern for resetting state when props change
  const imageKey = useMemo(() => src || 'no-src', [src])

  // Determine if image is landscape (for smart fit)
  const isLandscape = naturalDimensions
    ? naturalDimensions.width > naturalDimensions.height
    : true // Default to landscape assumption

  // Get computed object-fit based on fit strategy
  // Uses variant's defaultFit if fit prop is not explicitly provided
  const getObjectFit = (): string => {
    const effectiveFit = fit || config.defaultFit || 'cover'

    if (effectiveFit === 'smart') {
      // For smart fit: use contain for landscape, cover for portrait
      // This prevents cropping important content in landscape images
      return isLandscape ? 'object-contain' : 'object-cover'
    }
    return effectiveFit === 'cover' ? 'object-cover' : 'object-contain'
  }

  // Get variant-specific configuration
  const getVariantConfig = () => {
    switch (variant) {
      case 'card':
        return {
          containerClass: 'h-40 sm:h-48 w-full',
          aspectClass: aspectRatio
            ? (ASPECT_RATIOS[aspectRatio as AspectRatio] || `aspect-[${aspectRatio}]`)
            : '',
          shapeClass: 'rounded-t-lg',
          defaultFit: fit || 'cover',
          defaultSizes: sizes || '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
          useFill: true,
        }
      case 'avatar':
        return {
          containerClass: AVATAR_SIZES[size].className,
          aspectClass: '',
          shapeClass: 'rounded-full',
          defaultFit: fit || 'cover',
          defaultSizes: sizes || `${AVATAR_SIZES[size].width}px`,
          useFill: false,
          fixedDimensions: AVATAR_SIZES[size],
        }
      case 'thumbnail':
        return {
          containerClass: AVATAR_SIZES[size].className,
          aspectClass: 'aspect-square',
          shapeClass: 'rounded-lg',
          defaultFit: fit || 'cover',
          defaultSizes: sizes || `${AVATAR_SIZES[size].width}px`,
          useFill: false,
          fixedDimensions: AVATAR_SIZES[size],
        }
      case 'speaker':
        return {
          containerClass: AVATAR_SIZES[size].className,
          aspectClass: '',
          shapeClass: 'rounded-lg',
          defaultFit: fit || 'cover',
          defaultSizes: sizes || `${AVATAR_SIZES[size].width}px`,
          useFill: false,
          fixedDimensions: AVATAR_SIZES[size],
        }
      case 'partner':
        // Partner logos use object-contain to preserve logo aspect ratio
        return {
          containerClass: AVATAR_SIZES[size].className,
          aspectClass: '',
          shapeClass: 'rounded-lg',
          defaultFit: fit || 'contain',
          defaultSizes: sizes || `${AVATAR_SIZES[size].width}px`,
          useFill: false,
          fixedDimensions: AVATAR_SIZES[size],
        }
      case 'banner':
        return {
          containerClass: 'w-full',
          aspectClass: aspectRatio
            ? (ASPECT_RATIOS[aspectRatio as AspectRatio] || `aspect-[${aspectRatio}]`)
            : ASPECT_RATIOS.banner,
          shapeClass: 'rounded-lg',
          defaultFit: fit || 'cover',
          defaultSizes: sizes || '100vw',
          useFill: true,
        }
      case 'custom':
      default:
        return {
          containerClass: '',
          aspectClass: aspectRatio
            ? (ASPECT_RATIOS[aspectRatio as AspectRatio] || `aspect-[${aspectRatio}]`)
            : '',
          shapeClass: shape === 'circle' ? 'rounded-full' : shape === 'rounded' ? 'rounded-lg' : '',
          defaultFit: fit || 'cover',
          defaultSizes: sizes || '100vw',
          useFill: true,
        }
    }
  }

  const config = getVariantConfig()

  // Override shape if explicitly provided
  const finalShapeClass = shape !== undefined
    ? (shape === 'circle' ? 'rounded-full' : shape === 'rounded' ? 'rounded-lg' : '')
    : config.shapeClass

  // Ring classes
  const ringClasses = {
    none: '',
    default: 'ring-2 ring-gray-200',
    hover: 'ring-2 ring-gray-200 group-hover:ring-blue-300 transition-all',
  }

  // Default fallback based on variant
  const getDefaultFallback = (): ReactNode => {
    if (fallback) return fallback

    switch (variant) {
      case 'avatar':
        return <User className="w-1/2 h-1/2 text-gray-400" />
      case 'speaker':
        return <User className="w-1/2 h-1/2 text-gray-400" />
      case 'partner':
        return <Briefcase className="w-1/2 h-1/2 text-gray-400" />
      case 'card':
      case 'banner':
      case 'thumbnail':
      default:
        return <ImageIcon className="w-1/4 h-1/4 text-gray-300" />
    }
  }

  // Handle image load to get natural dimensions for smart fit
  const handleLoad = () => {
    setIsLoaded(true)
    if (fit === 'smart' && imgRef.current) {
      setNaturalDimensions({
        width: imgRef.current.naturalWidth,
        height: imgRef.current.naturalHeight,
      })
    }
  }

  // Show fallback if no src or error
  const showFallback = !src || hasError

  return (
    <div
      className={`
        relative overflow-hidden flex-shrink-0 flex items-center justify-center
        ${bgColor}
        ${config.containerClass}
        ${config.aspectClass}
        ${finalShapeClass}
        ${ringClasses[ring]}
        ${onClick ? 'cursor-pointer' : ''}
        ${hoverScale ? 'group' : ''}
        ${containerClassName}
      `.trim().replace(/\s+/g, ' ')}
      onClick={onClick}
    >
      {showFallback ? (
        // Fallback element
        <div className="flex items-center justify-center w-full h-full">
          {getDefaultFallback()}
        </div>
      ) : config.useFill ? (
        // Fill mode (for cards, banners)
        <Image
          key={imageKey}
          ref={imgRef}
          src={src}
          alt={alt}
          fill
          className={`
            ${getObjectFit()}
            ${hoverScale ? 'group-hover:scale-105 transition-transform duration-200' : ''}
            ${!isLoaded ? 'opacity-0' : 'opacity-100'}
            transition-opacity duration-200
            ${imageClassName}
          `.trim().replace(/\s+/g, ' ')}
          sizes={config.defaultSizes}
          priority={priority}
          onError={() => setHasError(true)}
          onLoad={handleLoad}
        />
      ) : (
        // Fixed dimensions mode (for avatars, thumbnails)
        <Image
          key={imageKey}
          ref={imgRef}
          src={src}
          alt={alt}
          width={config.fixedDimensions?.width || 40}
          height={config.fixedDimensions?.height || 40}
          className={`
            w-full h-full
            ${getObjectFit()}
            ${hoverScale ? 'group-hover:scale-105 transition-transform duration-200' : ''}
            ${!isLoaded ? 'opacity-0' : 'opacity-100'}
            transition-opacity duration-200
            ${imageClassName}
          `.trim().replace(/\s+/g, ' ')}
          priority={priority}
          onError={() => setHasError(true)}
          onLoad={handleLoad}
        />
      )}

      {/* Loading placeholder shimmer */}
      {!isLoaded && !showFallback && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse" />
      )}
    </div>
  )
}

/**
 * Export size constants for external use
 */
export { AVATAR_SIZES, ASPECT_RATIOS }
export type { ImageVariant, AvatarSize, AspectRatio, ObjectFit }
